import { QueryClient } from '@tanstack/react-query';
import { globalProperties } from './global';
import { ObjectType } from '../Types/base';

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: false,
			staleTime: 1000 * 60 * 5,
		},
	},
});

export async function copyToClipboard(text: string) {
	return await navigator.clipboard.writeText(text);
}

export async function copyHtmlToClipboard(html: string) {
	const blob = new Blob([html], { type: 'text/html' });
	const item = new ClipboardItem({ 'text/html': blob });
	return await navigator.clipboard.write([item]);
}

export function getRoute(type: ObjectType.Document, documentId: number, isArchived: boolean): string;
export function getRoute(type: ObjectType.DocumentCategory, categoryId: number, markedDocId?: number): string;
export function getRoute(type: ObjectType, ...args: unknown[]): string {
	switch (type) {
		case ObjectType.Document:
			return args[1] === true ? `/archive/${args[0]}` : `/document-view/${args[0]}`;
		case ObjectType.DocumentCategory:
			if (args.length === 2) {
				return `/document/list/${args[0]}?marked=${args[1]}`;
			}
			return `/document/list/${args[0]}`;
		default:
			throw new Error('Invalid object type');
	}
}

export async function get<T>(endpoint: string) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const response = await fetch(url, {
		method: 'GET',
		headers,
	});

	return handleResponse<T>(response);
}

export async function post<T>(endpoint: string, data?: object | number | boolean | string) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const { data: postData } = getPostData(data);

	if (postData) {
		headers['Content-Type'] = 'application/json';
		if (typeof postData === 'object') {
			fixDateProps(postData);
		}
	}

	const response = await fetch(url, {
		method: 'POST',
		headers,
		body: postData !== undefined ? JSON.stringify(postData) : undefined,
	});

	return handleResponse<T>(response);
}

export async function upload<T>(endpoint: string, file: File, fields?: Record<string, string>) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);

	const formData = new FormData();
	formData.append('file', file);
	if (fields) {
		for (const [key, value] of Object.entries(fields)) {
			formData.append(key, value);
		}
	}

	const response = await fetch(url, {
		method: 'POST',
		headers,
		body: formData,
	});

	return handleResponse<T>(response);
}

export async function httpDelete<T>(endpoint: string) {
	const { headers, url } = getBaseHeadersAndUrl(endpoint);
	const response = await fetch(url, {
		method: 'DELETE',
		headers,
	});

	return handleResponse<T>(response);
}

async function handleResponse<T>(response: Response) {
	if (!response.ok) {
		const errorBody = await response.text();
		throw new Error(errorBody || 'An unknown error occurred');
		//throw new Error(`HTTP error! Status: ${response.status}`);
	}

	const contentType = response.headers.get('Content-Type');
	if (contentType && contentType.includes('application/json')) {
		const result: T = await response.json();
		return result;
	}
	return null;
}

function getBaseHeadersAndUrl(endpoint: string) {
	const token = getToken();
	const url = getSiteRootPath() + endpoint;

	const headers: Record<string, string> = {
		'Biz-Origin': 'bizreact',
	};
	if (token) {
		headers['Auth-Token'] = token;
	}

	return { headers, url };
}

function getPostData(data?: object | number | boolean | string): { data?: object | number | boolean | string; isJson: boolean } {
	if (!data) {
		return { data: undefined, isJson: false };
	}
	if (typeof data === 'number' || typeof data === 'boolean' || typeof data === 'string') {
		return { data, isJson: false };
	} else if (data instanceof Array) {
		return { data: [...data], isJson: true };
	} else if (data) {
		return { data: { ...data }, isJson: true };
	}
	return { data: {}, isJson: false };
}

function fixDateProps<T extends object>(o: T): T {
	//todo: activate when needed, need to move date functions first to new frontend
	// Define an object that can be indexed with a string and holds any value
	// const objWithIndexSignature: { [key: string]: any } = o;

	// for (const p in objWithIndexSignature) {
	// 	if (objWithIndexSignature[p] instanceof Date) {
	// 		// Convert Date to string
	// 		objWithIndexSignature[p] = formatToSimpleDateTimeString(objWithIndexSignature[p]);
	// 	} else if (typeof objWithIndexSignature[p] === 'object' && objWithIndexSignature[p] !== null) {
	// 		// Recursive call for nested objects
	// 		fixDateProps(objWithIndexSignature[p]);
	// 	}
	// }

	return o;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getQSFromObject<T extends Record<string, any>>(params: T): URLSearchParams {
	return new URLSearchParams(
		Object.entries(params).map(([key, value]) => {
			let formattedValue = value;

			if (value instanceof Date) {
				// Format the date to ISO string or a specific format (e.g., yyyy-MM-dd)
				formattedValue = value.toISOString(); // ISO format: "2024-06-11T12:34:56.789Z"
				// Alternative format:
				// formattedValue = value.toISOString().split('T')[0]; // "yyyy-MM-dd"
			}

			return [key, String(formattedValue)];
		})
	);
}

export function hardRedirect(relativePath: string) {
	location.href = getSiteRootPath() + relativePath;
}

export function reloadPage() {
	location.href = getSiteRootPath();
}

export function getToken() {
	const token = localStorage.getItem('BizPartAuthToken');
	if (token) return token;

	return sessionStorage.getItem('BizPartAuthToken');
}

// export function deleteCookie(name: string) {
// 	document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
// }

export function removeToken() {
	sessionStorage.removeItem('BizPartAuthToken');
	localStorage.removeItem('BizPartAuthToken');
	//deleteCookie('BizPartAuthToken');
}

export function setToken(token: string, userId?: number) {
	removeToken();

	if (userId && userId > 0) {
		localStorage.setItem('BizPartAuthToken', token);
	} else {
		sessionStorage.setItem('BizPartAuthToken', token);
	}
}

export function getPathWithRootPath(path: string) {
	if (path.startsWith('http')) return path;

	path = path.replace('##UPLOAD##/', getPortalUploadPath());

	const portalAlias = getPortalAlias();
	const override = getLocalStorage(`override_siteroot_${portalAlias}`.toLowerCase());
	if (portalAlias.length > 0 && (override?.length ?? 0) > 0) {
		if (path.toLowerCase().indexOf('getfilehandler.ashx') >= 0) {
			return `${override}${path.replace('~/', '')}`;
		}
	}

	let trimmedPath = path;
	if (trimmedPath.startsWith('~')) {
		trimmedPath = trimmedPath.substring(1);
	}
	if (trimmedPath.startsWith('/')) {
		trimmedPath = trimmedPath.substring(1);
	}
	return getSiteRootPath() + trimmedPath;
}

export function getSiteRootPath() {
	//globalProperties is not working in old frontend/admin, so we need this as fallback :( (for now)
	return globalProperties.siteRootPath ?? window.siteRootPath;
}

export function getPortalUploadPath() {
	return globalProperties.portalUploadPath ?? window.portalUploadPath;
}

export function getCultureCode() {
	return globalProperties.cultureCode ?? window.cultureCode;
}

export function getPortalAlias() {
	return globalProperties.portalAlias ?? window.portalAlias;
}

export function getPortalId() {
	return globalProperties.portalId ?? window.portalId;
}

export function getPortalVersion() {
	return globalProperties.portalVersion ?? window.portalVersion;
}

export function getLocalStorage(key: string) {
	const settingKey = `${key}_${getPortalId()}`;
	return localStorage.getItem(settingKey);
}

export function setLocalStorage(key: string, value: string) {
	const settingKey = `${key}_${getPortalId()}`;
	localStorage.setItem(settingKey, value);
}

export function getTransformedRootPath() {
	const mode = import.meta.env.MODE;
	if (mode === 'dev-live-server') {
		return 'http://localhost:3000/src/';
	}

	const normalRootPath = getSiteRootPath();
	return `${normalRootPath}dist/react/`;
}

export function getTransformedPath(path: string) {
	const rootPath = getTransformedRootPath();
	if (!path) return rootPath;

	return `${rootPath}${path.startsWith('/') ? path.substring(1) : path}`;
}
